import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Mail, Sparkles, Users } from "lucide-react";

// INR → USD conversion (approximate, as of Feb 2026: ~₹83 / USD)
const PROFESSIONAL_INR = "₹899";
const PROFESSIONAL_USD = "~$11";
const TEAM_INR = "₹2,499";
const TEAM_USD = "~$30";

const PLAN_FEATURES = [
  ["Full CommPass access", true, true],
  ["All AAC disorders", true, true],
  ["AI-powered activity generation", true, true],
  ["AI word list generation", true, true],
  ["Complete goal programming framework", true, true],
  ["Single user account", true, false],
  ["Up to 3 users included", false, true],
  ["Centralised billing", false, true],
];

const Cell = ({ on }) =>
  on ? (
    <span className="inline-flex w-7 h-7 rounded-full bg-[#E6F0E9] text-[#2C4C3B] items-center justify-center">
      <Check className="w-4 h-4" />
    </span>
  ) : (
    <span className="inline-block w-7 h-7 rounded-full bg-[#F4F5F0] text-[#52606D]/60 leading-7 text-center text-sm">—</span>
  );

export default function Plans() {
  return (
    <div className="organic-bg" data-testid="plans-page">
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-10 text-center">
        <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Plans &amp; Pricing</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C4C3B] mt-3 leading-[1.05]">
          Choose your <span className="text-[#C66A51]">CommPass</span> plan.
        </h1>
        <p className="text-lg text-[#52606D] mt-5 max-w-2xl mx-auto leading-relaxed">
          Built for SLPs, educators, and AAC specialists. Start with Professional or scale your practice with a Team &amp; Centre plan.
        </p>
      </section>

      {/* Plan cards */}
      <section className="max-w-6xl mx-auto px-6 pb-20 grid lg:grid-cols-2 gap-6">
        {/* Professional */}
        <article className="p-8 rounded-3xl bg-white border border-[#E2E8F0] card-lift" data-testid="plan-professional">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#52606D]">
            <Sparkles className="w-3.5 h-3.5" /> Professional
          </div>
          <h2 className="font-display text-3xl text-[#2C4C3B] mt-4">For individual clinicians</h2>
          <p className="text-sm text-[#52606D] mt-2">
            Built for speech-language pathologists, educators and professionals who want complete AAC programming with AI assistance.
          </p>
          <div className="mt-6 flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-5xl text-[#2C4C3B]" data-testid="professional-price">{PROFESSIONAL_INR}</span>
            <span className="text-sm text-[#52606D]">/month</span>
            <span className="text-sm text-[#52606D] ml-2" data-testid="professional-usd">({PROFESSIONAL_USD} USD)</span>
          </div>
          <p className="text-xs text-[#52606D] mt-1">Billed monthly. Cancel anytime.</p>
          <Button className="mt-6 w-full btn-primary rounded-full py-6" data-testid="professional-cta">
            Start Professional
          </Button>
          <p className="mt-8 text-xs uppercase tracking-[0.15em] text-[#52606D]">Includes:</p>
          <ul className="mt-3 space-y-3 text-sm text-[#52606D]">
            {[
              "Full CommPass access",
              "All AAC disorders",
              "AI-powered activity generation",
              "AI word list generation",
              "Complete goal programming framework",
              "Single user account",
            ].map((f) => (
              <li key={f} className="flex gap-3">
                <Check className="w-4 h-4 text-[#2C4C3B] mt-0.5 flex-shrink-0" /> <span>{f}</span>
              </li>
            ))}
          </ul>
        </article>

        {/* Team & Centre */}
        <article className="relative p-8 rounded-3xl bg-[#2C4C3B] text-white border border-[#2C4C3B] shadow-xl" data-testid="plan-team">
          <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-[#C66A51] text-white text-[10px] tracking-[0.2em] uppercase font-semibold">Most popular</span>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase opacity-80">
            <Users className="w-3.5 h-3.5" /> Team &amp; Centre
          </div>
          <h2 className="font-display text-3xl mt-4">For clinics, schools and therapy centres</h2>
          <p className="text-sm opacity-90 mt-2">
            Everything in Professional, with collaborative tools for teams of up to 3 users.
          </p>
          <div className="mt-6 flex items-baseline gap-2 flex-wrap">
            <span className="font-display text-5xl" data-testid="team-price">{TEAM_INR}</span>
            <span className="text-sm opacity-80">/month</span>
            <span className="text-sm opacity-80 ml-2" data-testid="team-usd">({TEAM_USD} USD)</span>
          </div>
          <p className="text-xs opacity-80 mt-1">Billed monthly. Cancel anytime.</p>
          <Button className="mt-6 w-full rounded-full py-6 bg-[#C66A51] hover:bg-[#A85639] text-white" data-testid="team-cta">
            Start Team Plan
          </Button>
          <p className="mt-8 text-xs uppercase tracking-[0.15em] opacity-80">Includes:</p>
          <ul className="mt-3 space-y-3 text-sm">
            {[
              "Full CommPass access",
              "All AAC disorders",
              "AI-powered activity generation",
              "AI word list generation",
              "Complete goal programming framework",
              "Up to 3 users included",
              "Centralised billing",
            ].map((f) => (
              <li key={f} className="flex gap-3">
                <Check className="w-4 h-4 text-[#A8BAA3] mt-0.5 flex-shrink-0" /> <span>{f}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      {/* Comparison */}
      <section className="bg-[#F4F5F0] py-20" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-6">
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Side by side</span>
          <h2 className="font-display text-3xl text-[#2C4C3B] mt-2">Feature comparison</h2>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-white text-left">
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.1em] text-[#52606D]">Feature</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.1em] text-[#52606D] text-center">Professional</th>
                  <th className="px-5 py-4 text-xs uppercase tracking-[0.1em] text-[#52606D] text-center">Team &amp; Centre</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_FEATURES.map(([name, pro, team], i) => (
                  <tr key={i} className="border-t border-[#E2E8F0]">
                    <td className="px-5 py-3.5 text-[#1A202C]">{name}</td>
                    <td className="px-5 py-3.5 text-center"><Cell on={pro} /></td>
                    <td className="px-5 py-3.5 text-center"><Cell on={team} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 p-6 rounded-2xl bg-white border border-[#E2E8F0] flex flex-wrap items-center gap-4 justify-between" data-testid="institution-cta">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E6F0E9] text-[#2C4C3B] flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-lg text-[#2C4C3B]">Institutional &amp; NGO pricing</div>
                <p className="text-sm text-[#52606D]">For institutional bulk pricing or NGO rates, write to us.</p>
              </div>
            </div>
            <a href="mailto:sensiiq.services@gmail.com" className="text-sm text-[#2C4C3B] font-medium hover:underline" data-testid="institution-email">
              sensiiq.services@gmail.com →
            </a>
          </div>

          <p className="mt-6 text-xs text-[#52606D] text-center">
            Prices in INR. USD conversions are approximate at prevailing exchange rates (~₹83/USD). International users may be billed in USD at the equivalent rate. By subscribing you agree to our{" "}
            <Link to="/clinical-notes#terms" className="underline">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
